import { Typography, Box } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Icon from 'src/units/Icon';
import { Container } from './styles';
import {
  ContentSection,
  Description,
  ScrollWrapper,
  Title,
  TooltipButton,
  TopSection,
} from '../custom-tooltip/styles';
import { DeviceManager } from 'src/utils';
import { IotList } from './components/IotList/IotList';
import { EIoTProviders, TIoTDevice } from 'src/components/menu-wrapper/components/iot-menu/types';
import { IotItem } from './types';
import { renderMediaContent } from 'src/components/tag-tooltip/config';

export const IotTagTooltip = ({
  setIotModalOpen,
  tag,
  providers,
  handleUnlock,
  iotDevices,
  settings = { adminPanelAccess: false },
}) => {
  const [isClamped, setIsClamped] = useState(false);
  const [inFullScreen, setInFullScreen] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const rombusProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.RHOMBUS),
    [providers],
  );
  const kisiProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.Kisi),
    [providers],
  );

  const tanklogixProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.TANKLOGIX),
    [providers],
  );

  const thingWorxProvider = useMemo(
    () => providers?.find((provider) => provider?.provider?.name === EIoTProviders.THINGWORX),
    [providers],
  );

  const { doors, sensors, cameras, engines, things } = useMemo(() => {
    const msSqlDevices: TIoTDevice | IotItem[] = [];
    const rombusDevices: TIoTDevice[] = [];
    const kisiDevices: TIoTDevice | IotItem[] = [];
    const tanklogixDevices: TIoTDevice | IotItem[] = [];
    const thingWorxDevices: TIoTDevice | IotItem[] = [];

    tag?.iotDevices?.forEach((device) => {
      if (device.providerId == rombusProvider?.providerId) {
        rombusDevices.push(device);
      } else if (device.providerId == kisiProvider?.providerId) {
        kisiDevices.push({ ...device, type: 'door', onClick: () => handleUnlock(device) });
      } else if (device.providerId == tanklogixProvider?.providerId) {
        const foundDevice = iotDevices.find((d) => d.id === device.id);
        tanklogixDevices.push({ ...foundDevice, type: 'engine' });
      } else if (device.providerId == thingWorxProvider?.providerId) {
        const foundDevice = iotDevices.find((d) => d.externalDeviceId === device.externalDeviceId);

        let alertStatus = null;
        try {
          const iotAlert = localStorage.getItem('iotAlert');
          if (iotAlert) {
            const alerts = JSON.parse(iotAlert);
            alertStatus = alerts.find((alert) => alert.deviceId === String(foundDevice?.id)) || null;
          }
        } catch (error) {
          console.error('Error parsing iotAlert from localStorage:', error);
        }

        thingWorxDevices.push({ ...foundDevice, type: 'thing', alert: alertStatus });
      } else {
        msSqlDevices.push({ ...device, type: 'sensor' });
      }
    });

    return {
      doors: kisiDevices,
      cameras: rombusDevices,
      sensors: msSqlDevices,
      engines: tanklogixDevices,
      things: thingWorxDevices,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag, rombusProvider, kisiProvider, tanklogixProvider, iotDevices, thingWorxProvider]);

  const trimmedDescription = useMemo(() => {
    return tag?.description?.length > 250 ? tag?.description?.slice(0, 250) : tag?.description;
  }, [tag]);

  const handleExpandButtonClick = () => {
    setIotModalOpen(true, false);
  };

  const handleReadMoreClick = () => {
    setIotModalOpen(true, true);
  };

  const handleGalleryItemClick = () => {
    if (setIotModalOpen) setIotModalOpen(true, false);
  };

  const checkIfClamped = () => {
    const element = textRef.current;
    if (element) {
      const isOverflowing =
        element.scrollHeight > element.clientHeight || tag?.description?.length > 250;
      setIsClamped(isOverflowing);
    }
  };

  const toggleFullScreen = (ref) => {
    if (!document.fullscreenElement) {
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else {
      if (document.exitFullscreen) {
        setInFullScreen(false);
        document.exitFullscreen();
      }
    }
  };
  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  useLayoutEffect(() => {
    checkIfClamped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag]);

  return (
    <Container>
      <TopSection>
        <Title>{tag?.mpTitle && tag?.title}</Title>
        <TooltipButton onClick={handleExpandButtonClick}>
          <Icon.Expand />
        </TooltipButton>
      </TopSection>
      <ScrollWrapper style={{ maxHeight: DeviceManager.isMobile() ? '360px' : '500px' }}>
        {tag?.description && (
          <ContentSection>
            <Description
              dangerouslySetInnerHTML={{
                __html: trimmedDescription,
              }}
              ref={textRef}
            />
            {isClamped && (
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={handleReadMoreClick}
              >
                {` Read more`}
              </Typography>
            )}
          </ContentSection>
        )}
        {!![...doors, ...sensors, ...engines, ...things]?.length && (
          <ContentSection>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>IoT</Typography>
            <IotList
              items={[...doors, ...sensors, ...engines, ...things]}
              settings={{ measureNameMaxWidth: '215px' }}
            />
          </ContentSection>
        )}
        {!!cameras?.length && (
          <ContentSection>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Media</Typography>
            {cameras.map((camera) => {
              return (
                camera.videoUrl && (
                  <Box width="100%" height="205px">
                    <iframe
                      src={camera.videoUrl}
                      allow="autoplay; fullscreen"
                      allowFullScreen
                      title="Embedded video"
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                )
              );
            })}
          </ContentSection>
        )}
        {tag?.tagGalleries?.length || (tag?.videoUrl && tag?.videoUrl !== 'null') ? (
          <ContentSection>
            {renderMediaContent(
              tag,
              handleGalleryItemClick,
              settings,
              inFullScreen,
              toggleFullScreen,
            )}
          </ContentSection>
        ) : null}
      </ScrollWrapper>
    </Container>
  );
};
